import { createContext, ReactNode, useCallback, useContext, useState } from "react";

export type FeedExpandTarget = {
  // One or both of these. If `releaseUuid` is set, FeedFullscreenPreview
  // skips the slug → uuid resolution step. Compact-card flows pass `slug`
  // only; the feature comic_release card passes both.
  releaseUuid?: string;
  slug?: string;
  readingDirection?: string;
  // Slot index to mount the fullscreen viewer at — 0 = cover, 1+ for page
  // indices in the deck. The active feature card passes its current
  // activePage so the fullscreen opens where the user already is.
  initialSlotIndex?: number;
};

type FeedExpandValue = {
  target: FeedExpandTarget | null;
  open: (target: FeedExpandTarget) => void;
  close: () => void;
};

const FeedExpandContext = createContext<FeedExpandValue | null>(null);

/**
 * Provides open/close control for the suggestion feed's fullscreen preview
 * to any descendant card. SuggestionFeed wraps its tree with this provider
 * and renders FeedFullscreenPreview using the same target.
 *
 * Cards consume via `useFeedExpand()`. Cards that don't expand anything
 * just don't call the hook.
 */
export function FeedExpandProvider({ children }: { children: ReactNode }) {
  const [target, setTarget] = useState<FeedExpandTarget | null>(null);

  const open = useCallback((next: FeedExpandTarget) => {
    setTarget(next);
  }, []);

  const close = useCallback(() => {
    setTarget(null);
  }, []);

  return (
    <FeedExpandContext.Provider value={{ target, open, close }}>
      {children}
    </FeedExpandContext.Provider>
  );
}

export function useFeedExpand() {
  const ctx = useContext(FeedExpandContext);
  if (!ctx) {
    // Outside the suggestion feed, expand has no effect. Returning a no-op
    // makes cards reusable in places that don't host the preview without
    // having to thread an optional prop through.
    return {
      target: null,
      open: () => {},
      close: () => {},
    } as FeedExpandValue;
  }
  return ctx;
}
